import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, MailCheck, MailOpen, MousePointerClick, MailX, ShieldOff, Mail, ListChecks } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

const pct = (num, den) => (den ? `${Math.round((num / den) * 100)}%` : "—");

function Kpi({ icon: Icon, label, value, sub, tint = "text-slate-700", testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className={`h-4 w-4 ${tint}`} />
        <span className="text-[11px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="font-heading text-2xl font-bold mt-1.5">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

function MiniBar({ value }) {
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-[#f04935]" style={{ width: value === "—" ? 0 : value }} />
      </div>
      <span className="text-xs font-mono text-slate-600 w-9 text-right">{value}</span>
    </div>
  );
}

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600", scheduled: "bg-amber-50 text-amber-700",
  sending: "bg-blue-50 text-blue-700", sent: "bg-emerald-50 text-emerald-700", failed: "bg-red-50 text-red-600",
};
const AUDIENCE_LABELS = { contacts: "Contacts", leads: "Leads", dealers: "Dealers", universe: "Universe", custom: "Custom list", adhoc: "Quick send", brevo: "Brevo audience" };

export default function MarketingDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["marketing-dashboard"],
    queryFn: async () => (await api.get("/marketing/dashboard")).data,
    refetchInterval: 60000,
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const k = data?.kpi || {};
  const t = data?.totals || {};
  const st = data?.campaign_status || {};
  const activity = data?.activity || [];
  const recent = data?.recent_campaigns || [];

  return (
    <div data-testid="marketing-dashboard">
      <PageHeader title="Marketing Dashboard" subtitle="Delivery, engagement and campaign performance at a glance." />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={Send} label="Emails Sent" value={(k.sent ?? 0).toLocaleString("en-IN")} sub={k.failed ? `${k.failed} failed` : "incl. Brevo campaigns"} testid="kpi-sent" />
        <Kpi icon={MailCheck} label="Delivered" value={(k.delivered ?? 0).toLocaleString("en-IN")} sub={`${pct(k.delivered, k.sent)} of sent`} tint="text-emerald-600" testid="kpi-delivered" />
        <Kpi icon={MailOpen} label="Opened" value={(k.opened ?? 0).toLocaleString("en-IN")} sub={`${pct(k.opened, k.delivered)} open rate`} tint="text-blue-600" testid="kpi-opened" />
        <Kpi icon={MousePointerClick} label="Clicked" value={(k.clicked ?? 0).toLocaleString("en-IN")} sub={`${pct(k.clicked, k.delivered)} click rate`} tint="text-[#f04935]" testid="kpi-clicked" />
        <Kpi icon={MailX} label="Bounced" value={(k.bounced ?? 0).toLocaleString("en-IN")} sub={`${pct(k.bounced, k.sent)} of sent`} tint="text-amber-600" testid="kpi-bounced" />
        <Kpi icon={ShieldOff} label="Opt-outs" value={t.optouts ?? 0} sub={`${k.unsubscribed ?? 0} via campaigns`} tint="text-slate-500" testid="kpi-optouts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-5 lg:col-span-2" data-testid="activity-chart-card">
          <h3 className="font-heading font-semibold text-sm mb-1">Last 30 Days — Engagement</h3>
          <p className="text-[11px] text-slate-400 mb-3">Deliveries, opens and clicks per day (from Brevo events).</p>
          {activity.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">No engagement events in the last 30 days — send a campaign to see activity here.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={activity} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
                <Area type="monotone" dataKey="opened" name="Opened" stroke="#3b82f6" fill="#3b82f622" strokeWidth={2} />
                <Area type="monotone" dataKey="clicked" name="Clicked" stroke="#f04935" fill="#f0493522" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5" data-testid="status-summary-card">
          <h3 className="font-heading font-semibold text-sm mb-3">Pipeline & Assets</h3>
          <div className="space-y-2.5">
            {[["Campaigns", t.campaigns ?? 0, Send], ["Email templates", t.templates ?? 0, Mail], ["Audience lists", t.lists ?? 0, ListChecks]].map(([label, val, Icon]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600"><Icon className="h-3.5 w-3.5 text-slate-400" />{label}</span>
                <span className="font-mono font-semibold">{val}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-4 pt-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">Campaigns by status</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(st).map(([s, n]) => (
                <span key={s} className={`text-[11px] font-semibold px-2 py-1 rounded-full capitalize ${STATUS_STYLES[s] || "bg-slate-100 text-slate-600"}`} data-testid={`status-chip-${s}`}>{s} · {n}</span>
              ))}
              {Object.keys(st).length === 0 && <span className="text-xs text-slate-400">No campaigns yet</span>}
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 mt-4" data-testid="recent-campaigns-card">
        <h3 className="font-heading font-semibold text-sm mb-3">Recent Campaigns</h3>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No campaigns yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">Campaign</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Audience</th>
                  <th className="py-2 pr-3 font-medium">Recipients</th>
                  <th className="py-2 pr-3 font-medium">Open rate</th>
                  <th className="py-2 pr-3 font-medium">Click rate</th>
                  <th className="py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} onClick={() => navigate(c.origin === "brevo" ? `/marketing/brevo-campaign/${String(c.id).replace("brevo-", "")}` : `/marketing/campaigns/${c.id}`)} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`dash-campaign-${c.id}`}>
                    <td className="py-2.5 pr-3 font-medium text-slate-800 max-w-[260px] truncate">
                      {c.name}
                      {c.origin === "brevo" && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 align-middle">Brevo</span>}
                    </td>
                    <td className="py-2.5 pr-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span></td>
                    <td className="py-2.5 pr-3 text-slate-500">{AUDIENCE_LABELS[c.audience_source] || c.audience_source}</td>
                    <td className="py-2.5 pr-3 font-mono">{c.stats?.total ?? 0}</td>
                    <td className="py-2.5 pr-3"><MiniBar value={pct(c.stats?.opened, c.stats?.delivered)} /></td>
                    <td className="py-2.5 pr-3"><MiniBar value={pct(c.stats?.clicked, c.stats?.delivered)} /></td>
                    <td className="py-2.5 text-slate-500 whitespace-nowrap">{formatDate(c.sent_at || c.scheduled_at || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
